import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Row from '../../components/Row';
import Col from '../../components/Col';
import HeadingWithDetails from '../../components/HeadingWithDetails';
import Input from '../../components/Input';
import { Multiselect } from 'multiselect-react-dropdown';
import Checkbox from '../../components/Checkbox';
import TextEditor from '../../components/Editor/Editor';
import Breadcrumb from '../../components/Breadcrumb';
import Callout from 'src/components/InformationCallout/Callout';
import AttachmentList from 'src/components/AttachmentList/AttachmentList';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import { PATHS } from 'src/common/constants';
import {
  FetchAnnouncementByIdResult,
  useAnnouncementsAPI,
} from './CustomHooks/useAnnouncementsAPI';
import AnnouncementViewSkeleton from './skeletons/AnnouncementViewSkeleton';
import './Announcementsview.scss';

interface AnnouncementsviewProps {
  match: any;
}

interface AnnouncementsviewState {
  formData: any;
  loader: boolean;
}

const Announcementsview = ({ match }: AnnouncementsviewProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const { fetchAnnouncementById } = useAnnouncementsAPI();

  const [state, setStateRaw] = useState<AnnouncementsviewState>(() => ({
    formData: {},
    loader: true,
  }));

  const setState = (patch: Partial<AnnouncementsviewState>) =>
    setStateRaw((prev: AnnouncementsviewState) => ({ ...prev, ...patch }));

  useEffect(() => {
    const loadAnnouncement = async () => {
      const result: FetchAnnouncementByIdResult = await fetchAnnouncementById(
        match.params.id
      );

      if (!result.ok) {
        setState({ loader: false });

        return;
      }

      setState({ formData: result.data, loader: false });
    };

    void loadAnnouncement();
  }, []);

  const announcementFormData = state.formData;

  const role: string | undefined = user?.roles?.[0]?.role?.name;

  const homeOwnerCheck =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === 'Home Owner' ||
      user.roles[0].role.name === 'Occupant');

  const canManageAnnouncements: boolean =
    role === 'Admin' ||
    role === 'Super Admin' ||
    role === 'Property Developer';

  const attachments =
    announcementFormData && announcementFormData.attachments
      ? JSON.parse(announcementFormData.attachments)
      : [];

  // legacy/workaround: view attachments are { title, url }; adapt to the shared
  // AttachmentList shape ({ name, path }).
  const viewAttachments = attachments.map((attachment: any) => ({
    name: attachment.title,
    path: attachment.url,
  }));

  const buildings = announcementFormData.buildings
    ? announcementFormData.buildings.length > 0
      ? announcementFormData.buildings.map((item: any) => ({
          id: item.buildingLevelId,
          name: item.name,
        }))
      : []
    : [];

  const levels = announcementFormData.levels
    ? announcementFormData.levels.length > 0
      ? announcementFormData.levels.map((item: any) => ({
          id: item.buildingLevelId,
          name: item.name,
        }))
      : []
    : [];

  const units = announcementFormData.announcementSuites
    ? announcementFormData.announcementSuites.length > 0
      ? announcementFormData.announcementSuites.map((item: any) => ({
          id: item.unitId,
          name: item.name,
        }))
      : []
    : [];

  const title: string = announcementFormData.title || 'Announcement';

  return (
    <WithNavigation
      title={title}
      top={
        <HeadingWithDetails
          breadcrumb={
            <Breadcrumb to={PATHS.ANNOUNCEMENTS} label="Announcements" />
          }
          headline={title}
          items={[]}
        />
      }
      bottom={
        <div className="announcement-view-body">
          {state.loader ? (
            <AnnouncementViewSkeleton />
          ) : (
            <div className="announcement-view">
            {canManageAnnouncements && (
              <Callout
                variant="info"
                title="This announcement is read-only"
                subtitle="Announcements send an email to recipients as soon as they're created, so they can't be edited afterward — create a new one to share an update."
                className="announcement-view-callout"
              />
            )}
            {homeOwnerCheck ? (
              <Row>
                <Col xs="12" md="6">
                  <Input
                    value={announcementFormData.propertyName}
                    label="Property Name"
                    disabled={true}
                    block
                  />
                  <Input
                    value={announcementFormData.title}
                    label="Title"
                    disabled={true}
                    block
                  />

                  <div className="input">
                    <div className="input__require">
                      <label className="input__label">Description</label>
                    </div>

                    <div
                      style={{
                        borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
                      }}
                      className="input__control"
                    >
                      <TextEditor
                        editorState={announcementFormData.description}
                        readOnly={true}
                      />
                    </div>
                  </div>
                  <AttachmentList
                    attachments={viewAttachments}
                    readOnly
                    clickable
                  />
                </Col>
              </Row>
            ) : (
              <Row>
                <Col xs="12" md="6">
                  <Input
                    value={announcementFormData.propertyName}
                    label="Property Name"
                    disabled={true}
                    block
                  />
                  <Row>
                    <Col xs="12" md="3">
                      <label
                        htmlFor="Building"
                        className="announcement-view__plan-toggle"
                        style={{ padding: '0px 20px 0px 5px' }}
                      >
                        <Checkbox
                          id="Buildings"
                          name="plan"
                          value={announcementFormData.isBuilding}
                          disabled={true}
                          block
                          checked={announcementFormData.isBuilding}
                        />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Buildings
                      </label>
                    </Col>
                    <Col xs="12" md="6">
                      <label
                        htmlFor="Floor"
                        className="announcement-view__plan-toggle"
                        style={{ padding: '0px 20px 0px 5px' }}
                      >
                        <Checkbox
                          id="Floor/Level"
                          name="plan"
                          value={announcementFormData.isLevel}
                          disabled={true}
                          block
                          checked={announcementFormData.isLevel}
                        />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Floor/Level
                      </label>
                    </Col>
                  </Row>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '10px',
                      }}
                    >
                      <div
                        className="reporting__heading"
                        style={{ color: 'black' }}
                      >
                        {announcementFormData.isBuilding
                          ? 'Buildings'
                          : 'Floor/Level'}
                      </div>
                    </div>

                    <Multiselect
                      id="building"
                      selectedValues={
                        announcementFormData.isBuilding ? buildings : levels
                      }
                      displayValue="name"
                      disable={true}
                    />
                  </div>
                  <br />
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      className="reporting__heading"
                      style={{ color: 'black' }}
                    >
                      Unit(s)
                    </div>
                  </div>
                  <Multiselect
                    selectedValues={units}
                    displayValue="name"
                    disable={true}
                  />
                  <br />
                </Col>
                <Col xs="12" md="6">
                  <Input
                    value={announcementFormData.title}
                    label="Title"
                    disabled={true}
                    block
                  />
                  <div className="input">
                    <div className="input__require">
                      <label className="input__label">Description</label>
                    </div>

                    <div
                      style={{
                        borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
                        marginTop: '-7px',
                      }}
                      className="input__control"
                    >
                      <TextEditor
                        editorState={announcementFormData.description}
                        readOnly={true}
                      />
                    </div>
                  </div>

                  <AttachmentList
                    attachments={viewAttachments}
                    readOnly
                    clickable
                  />
                </Col>
              </Row>
            )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default Announcementsview;
